import { useState } from 'react';
import { Sun, Moon, Monitor, ChevronDown } from 'lucide-react';
import type { Theme } from '../types';
import { Button } from './ui/Button';

interface ThemeToggleProps {
  theme: Theme;
  onChange: (theme: Theme) => void;
  className?: string;
  variant?: 'icon' | 'dropdown' | 'button';
}

const themeConfig = {
  light: { icon: Sun, label: 'Light', color: 'text-yellow-500' },
  dark: { icon: Moon, label: 'Dark', color: 'text-indigo-400' },
  system: { icon: Monitor, label: 'System', color: 'text-gray-500' },
} as const;

type ThemeKey = keyof typeof themeConfig;

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  theme,
  onChange,
  className = '',
  variant = 'icon',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const resolvedTheme: ThemeKey = theme === 'light' || theme === 'dark' || theme === 'system' ? theme : 'light';

  const currentConfig = themeConfig[resolvedTheme];

  const cycleTheme = () => {
    const order: ThemeKey[] = ['light', 'dark', 'system'];
    const currentIndex = order.indexOf(resolvedTheme);
    const nextIndex = (currentIndex + 1) % order.length;
    onChange(order[nextIndex]);
  };

  if (variant === 'dropdown') {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
        >
          <currentConfig.icon className={`w-4 h-4 ${currentConfig.color}`} />
          <span className="text-sm">{currentConfig.label}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
            {(Object.keys(themeConfig) as ThemeKey[]).map((t) => {
              const config = themeConfig[t];
              return (
                <button
                  key={t}
                  onClick={() => { onChange(t); setIsOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                    resolvedTheme === t
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <config.icon className={`w-4 h-4 ${config.color}`} />
                  <span className="capitalize">{config.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={cycleTheme}
        className={className}
        leftIcon={<currentConfig.icon className={`w-4 h-4 ${currentConfig.color}`} />}
      >
        {currentConfig.label}
      </Button>
    );
  }

  return (
    <button
      onClick={cycleTheme}
      className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${className}`}
      title={`Theme: ${currentConfig.label} (click to cycle)`}
    >
      <currentConfig.icon className={`w-5 h-5 ${currentConfig.color}`} />
    </button>
  );
};

export default ThemeToggle;